import React, { useEffect, useState } from "react";
import "./createLeadDialog.css";

const BDM_NAMES = ["Sandeep", "Jaspal", "Aditya"];

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED", "SGD", "AUD", "CAD", "THB", "JPY"];

const emptyForm = {
  // Lead meta
  createdBy: "Sandeep",
  source: "Website",
  priority: "Warm",
  // Customer
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  nationality: "",
  // Package
  travelType: "Leisure",
  destinationName: "",
  travelStartDate: "",
  travelEndDate: "",
  numberOfAdults: 1,
  numberOfChildren: 0,  // UI-only counter to build children array
  childrenAges: [],     // UI-only array of age strings
  numberOfInfants: 0,
  preferredCurrency: "USD",
  remarks: "",
};

function calcDuration(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  return days > 0 ? days : null;
}

export default function CreateLeadDialog({ open, onClose, onSubmit, isLoading, initialData }) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (initialData) {
      // Hydrate child ages from children array [{age}]
      const childrenAges = (initialData.children || []).map((c) => String(c.age));
      setForm({
        ...emptyForm,
        ...initialData,
        numberOfChildren: childrenAges.length,
        childrenAges,
      });
    } else {
      setForm(emptyForm);
    }
  }, [initialData, open]);

  if (!open) return null;

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    set(name, type === "number" ? (parseInt(value) || 0) : value);
  };

  const handleNumberOfChildren = (e) => {
    const count = Math.max(0, parseInt(e.target.value) || 0);
    setForm((f) => {
      const ages = [...f.childrenAges];
      while (ages.length < count) ages.push("");
      return { ...f, numberOfChildren: count, childrenAges: ages.slice(0, count) };
    });
  };

  const handleChildAge = (index, value) => {
    setForm((f) => {
      const ages = [...f.childrenAges];
      ages[index] = value;
      return { ...f, childrenAges: ages };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Build children array from ages
    const children = form.childrenAges.map((a) => ({ age: parseInt(a) || 0 }));
    const payload = {
      createdBy: form.createdBy,
      source: form.source,
      priority: form.priority,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      nationality: form.nationality,
      travelType: form.travelType,
      destinationName: form.destinationName,
      travelStartDate: form.travelStartDate || undefined,
      travelEndDate: form.travelEndDate || undefined,
      numberOfAdults: form.numberOfAdults,
      children,
      numberOfInfants: form.numberOfInfants,
      preferredCurrency: form.preferredCurrency,
      remarks: form.remarks,
    };
    onSubmit(payload);
  };

  const duration = calcDuration(form.travelStartDate, form.travelEndDate);
  const today = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="dialog-overlay">
      <div className="dialog-box">
        <div className="dialog-header">
          <h2>{initialData ? "Edit Lead" : "Add New Lead"}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Auto-generated fields preview */}
        <div className="auto-fields">
          <div className="auto-field">
            <span>Lead ID</span>
            <span>{initialData?.leadId || "Auto-generated"}</span>
          </div>
          <div className="auto-field">
            <span>Created On</span>
            <span>{initialData ? new Date(initialData.createdDate).toLocaleString("en-IN") : today}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {/* ── Lead Info ─────────────────────────────────────── */}
          <div className="section-title">Lead Information</div>
          <div className="form-grid">
            <div className="form-group">
              <label>Created By (BDM) *</label>
              <select name="createdBy" value={form.createdBy} onChange={handleChange} required>
                {BDM_NAMES.map((n) => <option key={n}>{n}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Lead Source</label>
              <select name="source" value={form.source} onChange={handleChange}>
                {["Website","Social Media","Email","Phone","Walk-in","Travel Expo","Referral","Other"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>Lead Priority</label>
              <div className="priority-group">
                {["Hot","Warm","Cold"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`priority-pill ${p.toLowerCase()}${form.priority === p ? " selected" : ""}`}
                    onClick={() => set("priority", p)}
                  >
                    {p === "Hot" ? "🔥" : p === "Warm" ? "☀️" : "❄️"} {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Customer Details ──────────────────────────────── */}
          <div className="section-title">Customer Details</div>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name *</label>
              <input name="firstName" value={form.firstName} onChange={handleChange} required placeholder="John" />
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Doe" />
            </div>

            <div className="form-group">
              <label>Email Address *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@example.com" />
            </div>

            <div className="form-group">
              <label>Contact Number</label>
              <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" />
            </div>

            <div className="form-group">
              <label>Nationality</label>
              <input name="nationality" value={form.nationality} onChange={handleChange} placeholder="e.g. Indian, British" />
            </div>
          </div>

          {/* ── Package Requirement ───────────────────────────── */}
          <div className="section-title">Package Requirement</div>
          <div className="form-grid">
            <div className="form-group">
              <label>Type of Travel</label>
              <select name="travelType" value={form.travelType} onChange={handleChange}>
                {["Leisure","Honeymoon","Group","Corporate","MICE","Adventure","Pilgrimage","Cultural","Other"].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Destination Name</label>
              <input name="destinationName" value={form.destinationName} onChange={handleChange} placeholder="e.g. Bali, Paris, Dubai" />
            </div>

            <div className="form-group">
              <label>Travel Start Date</label>
              <input type="date" name="travelStartDate" value={form.travelStartDate} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Travel End Date</label>
              <input type="date" name="travelEndDate" value={form.travelEndDate} onChange={handleChange} min={form.travelStartDate} />
            </div>

            {/* Duration auto-calc */}
            <div className="form-group full-width">
              {duration !== null && (
                <div className="duration-display">
                  ✈️ Travel Duration: <strong>{duration} day{duration !== 1 ? "s" : ""}</strong>
                  {duration >= 1 && ` (${duration - 1} night${duration - 1 !== 1 ? "s" : ""})`}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>No. of Adults</label>
              <input type="number" name="numberOfAdults" value={form.numberOfAdults} min="1" onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>No. of Children</label>
              <input type="number" name="numberOfChildren" value={form.numberOfChildren} min="0" onChange={handleNumberOfChildren} />
            </div>

            {/* Child age inputs */}
            {form.numberOfChildren > 0 && (
              <div className="form-group children-section">
                <label>Age of Each Child</label>
                <div className="children-ages">
                  {Array.from({ length: form.numberOfChildren }).map((_, i) => (
                    <div className="child-age-input" key={i}>
                      <label>Child {i + 1}</label>
                      <input
                        type="number"
                        min="0"
                        max="17"
                        value={form.childrenAges[i] || ""}
                        onChange={(e) => handleChildAge(i, e.target.value)}
                        placeholder="Age"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>No. of Infants</label>
              <input type="number" name="numberOfInfants" value={form.numberOfInfants} min="0" onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Preferred Currency</label>
              <select name="preferredCurrency" value={form.preferredCurrency} onChange={handleChange}>
                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>

            <div className="form-group full-width">
              <label>Remarks</label>
              <textarea
                name="remarks"
                rows="3"
                value={form.remarks}
                onChange={handleChange}
                placeholder="Any special requirements, preferences or additional notes..."
              />
            </div>
          </div>

          <div className="dialog-actions">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? "Saving..." : initialData ? "Update Lead" : "Create Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}